// ABOUTME: One line of the cart drawer: image, code and name, total list price, quantity stepper and remove.
// ABOUTME: Presentational; the drawer resolves the names and prices and owns the cart actions.

import {
    AddIcon,
    DeleteForeverOutlinedIcon,
    IconButton,
    RemoveIcon,
    Stack,
    Typography,
} from '@ds/coolshop';
import { useIntl } from 'react-intl';

import { CatalogThumbnail } from '../OfferLineItems/CatalogThumbnail';

// Side of the line item image, as in the cart visuals.
const THUMBNAIL_SIZE = '40px';

export interface CartLineItemRowProps {
    name: string;
    /** The model or prototype code, shown above the name; omitted when it adds nothing. */
    code?: string;
    imageUrl?: string;
    /** The line total, already formatted in the cart's currency. */
    totalPrice?: string;
    quantity: number;
    onQuantityChange: (quantity: number) => void;
    onRemove: () => void;
    /** While a cart change is in flight: each step reads the quantity of the last answer. */
    disabled?: boolean;
}

export function CartLineItemRow({
    name,
    code,
    imageUrl,
    totalPrice,
    quantity,
    onQuantityChange,
    onRemove,
    disabled = false,
}: CartLineItemRowProps) {
    const { formatMessage } = useIntl();

    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={2}
            py={1.5}
            borderBottom="1px solid"
            borderColor="divider"
            data-testid="cart-line-item"
        >
            <CatalogThumbnail url={imageUrl} alt={name} size={THUMBNAIL_SIZE} />
            <Stack flex={1} minWidth={0}>
                {code && code !== name && (
                    <Typography variant="subtitle3" color="primary.light" noWrap>
                        {code}
                    </Typography>
                )}
                <Typography variant="subtitle2" noWrap>
                    {name}
                </Typography>
            </Stack>
            {totalPrice && (
                <Stack flexShrink={0} alignItems="flex-start">
                    <Typography variant="caption" color="primary.light">
                        {formatMessage({
                            id: 'cpq.cart.totalListPrice',
                            defaultMessage: 'Total list price',
                        })}
                    </Typography>
                    <Typography variant="subtitle3">{totalPrice}</Typography>
                </Stack>
            )}
            <Stack direction="row" alignItems="center" flexShrink={0}>
                <IconButton
                    size="small"
                    color="secondary"
                    aria-label={formatMessage({
                        id: 'cpq.cart.decreaseQuantity',
                        defaultMessage: 'Decrease quantity',
                    })}
                    disabled={disabled || quantity <= 1}
                    onClick={() => onQuantityChange(quantity - 1)}
                >
                    <RemoveIcon fontSize="small" />
                </IconButton>
                {/* body2 at 700, as in the visuals: the DS Typography takes no fontWeight. */}
                <Typography variant="body2" minWidth={24} textAlign="center">
                    <strong>{quantity}</strong>
                </Typography>
                <IconButton
                    size="small"
                    color="secondary"
                    aria-label={formatMessage({
                        id: 'cpq.cart.increaseQuantity',
                        defaultMessage: 'Increase quantity',
                    })}
                    disabled={disabled}
                    onClick={() => onQuantityChange(quantity + 1)}
                >
                    <AddIcon fontSize="small" />
                </IconButton>
            </Stack>
            <IconButton
                size="small"
                aria-label={formatMessage({ id: 'common.remove', defaultMessage: 'Remove' })}
                disabled={disabled}
                onClick={onRemove}
            >
                <DeleteForeverOutlinedIcon fontSize="small" />
            </IconButton>
        </Stack>
    );
}
